import React from 'react';
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  Row,
} from 'react-bootstrap';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { SOURCE_STYLES } from './constants';
import {
  AnalyticsPeriod,
  BreakdownItem,
  InflowChartDatum,
} from './types';
import {
  getBreakdownTotal,
  getSeriesColor,
  getSourceLabel,
  getSourceStyle,
} from './utils';

export function SourceLegend() {
  const legendSources = Object.keys(SOURCE_STYLES);

  return (
    <div className="adsure-card mb-3">
      <div className="adsure-card-body">
        <div className="adsure-section-label">Test Source Legend</div>
        <div className="d-flex flex-wrap gap-2">
          {legendSources.map(source => {
            const { Icon, color } = getSourceStyle(source);

            return (
              <div key={source} className="adsure-source-chip">
                <span
                  className="adsure-source-icon"
                  style={{ background: `${color}18`, color }}
                >
                  <Icon />
                </span>
                <span className="small fw-semibold">{getSourceLabel(source)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function SummaryTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | number;
  tone?: string;
}) {
  return (
    <div className="adsure-card h-100">
      <div className="adsure-card-body">
        <div className="adsure-section-label">{label}</div>
        <div className="adsure-summary-value" style={tone ? { color: tone } : {}}>
          {value}
        </div>
      </div>
    </div>
  );
}

export function EmptyChartState({ message }: { message: string }) {
  return (
    <Alert variant="light" className="mb-0">
      {message}
    </Alert>
  );
}

export function SourcePills({
  sources,
  selectedSource,
  onChange,
}: {
  sources: string[];
  selectedSource: string;
  onChange: (value: string) => void;
}) {
  const options = ['all', ...sources];

  return (
    <div className="d-flex flex-wrap gap-2">
      {options.map(option => {
        const active = option === selectedSource;
        const label = option === 'all' ? 'All' : getSourceLabel(option);
        return (
          <Button
            key={option}
            size="sm"
            variant={active ? 'primary' : 'outline-secondary'}
            onClick={() => onChange(option)}
            className="adsure-pill-button"
          >
            {label}
          </Button>
        );
      })}
    </div>
  );
}

export function AnalyticsControls({
  period,
  startDate,
  endDate,
  sources,
  selectedSource,
  showPeriod = true,
  onPeriodChange,
  onStartDateChange,
  onEndDateChange,
  onSourceChange,
}: {
  period: AnalyticsPeriod;
  startDate: string;
  endDate: string;
  sources: string[];
  selectedSource: string;
  showPeriod?: boolean;
  onPeriodChange: (value: AnalyticsPeriod) => void;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  onSourceChange: (value: string) => void;
}) {
  return (
    <div className="adsure-card mb-3">
      <div className="adsure-card-body">
        <Row className="g-3 align-items-end">
          <Col md={6} lg={3}>
            <Form.Label className="adsure-section-label">Start Date</Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              max={endDate}
              onChange={event => onStartDateChange(event.target.value)}
            />
          </Col>
          <Col md={6} lg={3}>
            <Form.Label className="adsure-section-label">End Date</Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              min={startDate}
              onChange={event => onEndDateChange(event.target.value)}
            />
          </Col>
          {showPeriod ? (
            <Col md={4} lg={2}>
              <Form.Label className="adsure-section-label">View</Form.Label>
              <Form.Select
                value={period}
                onChange={event =>
                  onPeriodChange(event.target.value as AnalyticsPeriod)
                }
              >
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
              </Form.Select>
            </Col>
          ) : null}
          <Col md={12} lg={showPeriod ? 4 : 6}>
            <Form.Label className="adsure-section-label">Sources</Form.Label>
            <SourcePills
              sources={sources}
              selectedSource={selectedSource}
              onChange={onSourceChange}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
}

export function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: any[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;

  return (
    <div className="adsure-chart-tooltip">
      <div className="fw-semibold mb-2">{label}</div>
      {payload.map(item => (
        <div
          key={item.dataKey}
          className="small d-flex align-items-center gap-2 mb-1"
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 999,
              backgroundColor: item.color,
              display: 'inline-block',
            }}
          />
          <span>{item.name}:</span>
          <strong>{item.value}</strong>
        </div>
      ))}
    </div>
  );
}

export function DistributionTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: any[];
}) {
  if (!active || !payload?.length) return null;

  const item = payload[0];

  return (
    <div className="adsure-chart-tooltip">
      <div className="fw-semibold mb-1">{item.name}</div>
      <div className="small">
        Count: <strong>{item.value}</strong>
      </div>
    </div>
  );
}

export function InflowChart({
  title,
  subtitle,
  data,
  average,
  color,
}: {
  title: string;
  subtitle: string;
  data: InflowChartDatum[];
  average: number;
  color: string;
}) {
  if (!data.length) {
    return <EmptyChartState message="No incoming test records found for this date range." />;
  }

  return (
    <div className="adsure-card h-100">
      <div className="adsure-card-body">
        <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
          <div>
            <div className="fw-semibold">{title}</div>
            <div className="text-muted small mt-1">{subtitle}</div>
          </div>
          <Badge bg="light" text="dark">
            Avg {average.toFixed(1)}
          </Badge>
        </div>
        <div className="adsure-chart-frame">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 16, right: 12, left: 0, bottom: 8 }}>
              <defs>
                <linearGradient id="incomingTestsFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.36} />
                  <stop offset="100%" stopColor={color} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#e3e7ec" strokeDasharray="4 4" />
              <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: '#66707d' }} />
              <YAxis tick={{ fontSize: 11, fill: '#66707d' }} allowDecimals={false} />
              <Tooltip content={<ChartTooltip />} />
              <Legend />
              <ReferenceLine
                y={Number(average.toFixed(2))}
                stroke="#a50135"
                strokeDasharray="6 6"
                label={{
                  value: `Avg ${average.toFixed(1)}`,
                  position: 'insideTopRight',
                  fill: '#a50135',
                  fontSize: 11,
                }}
              />
              <Area
                type="monotone"
                dataKey="count"
                name="Incoming Tests"
                stroke={color}
                strokeWidth={3}
                fill="url(#incomingTestsFill)"
                activeDot={{ r: 6 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export function DistributionDonut({
  title,
  subtitle,
  data,
  centerLabel,
  frameCaption,
  controls,
  showCenterLabel = true,
}: {
  title: string;
  subtitle: string;
  data: BreakdownItem[];
  centerLabel: string;
  frameCaption?: string;
  controls?: React.ReactNode;
  showCenterLabel?: boolean;
}) {
  const visibleData = data.filter(item => item.count > 0);

  if (!visibleData.length) {
    return <EmptyChartState message="No records found for this selection." />;
  }

  const total = getBreakdownTotal(visibleData);

  return (
    <div className="adsure-card adsure-donut-card h-100">
      <div className="adsure-card-body">
        <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
          <div>
            <div className="fw-semibold">{title}</div>
            <div className="text-muted small mt-1">{subtitle}</div>
          </div>
          <Badge bg="light" text="dark">
            Total {total}
          </Badge>
        </div>
        <Row className="g-3 align-items-stretch adsure-donut-content">
          <Col xl={7}>
            <div className="adsure-chart-frame adsure-donut-frame">
              {frameCaption ? (
                <div className="adsure-donut-frame-caption" title={frameCaption}>
                  {frameCaption}
                </div>
              ) : null}
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={visibleData}
                    dataKey="count"
                    nameKey="label"
                    innerRadius={70}
                    outerRadius={118}
                    paddingAngle={3}
                    stroke="rgba(255,255,255,0.92)"
                    strokeWidth={2}
                  >
                    {visibleData.map((item, index) => (
                      <Cell key={item.key} fill={getSeriesColor(index)} />
                    ))}
                  </Pie>
                  <Tooltip content={<DistributionTooltip />} />
                  {showCenterLabel ? (
                    <text
                      x="50%"
                      y="47%"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize="14"
                      fill="#66707d"
                    >
                      {centerLabel}
                    </text>
                  ) : null}
                  <text
                    x="50%"
                    y={showCenterLabel ? '57%' : '52%'}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="28"
                    fontWeight="700"
                    fill="#414650"
                  >
                    {total}
                  </text>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Col>
          <Col xl={5}>
            <div className="adsure-donut-side">
              <div className="d-flex flex-column gap-2">
                {visibleData.map((item, index) => (
                  <div key={item.key} className="adsure-donut-legend-item">
                    <div className="d-flex align-items-center gap-2">
                      <span
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: 999,
                          display: 'inline-block',
                          backgroundColor: getSeriesColor(index),
                        }}
                      />
                      <span className="small fw-semibold">{item.label}</span>
                    </div>
                    <span className="small text-muted">{item.count}</span>
                  </div>
                ))}
              </div>
              {controls ? (
                <div className="adsure-donut-controls">{controls}</div>
              ) : null}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
}
